import React, { useEffect, useState, useRef } from "react";
import { Loader2 } from "lucide-react";
import axiosInstance from "../../../Config/axiosInstance";
import "./EditSSM.css";
import DatePicker, { registerLocale } from "react-datepicker";
import { ro } from "date-fns/locale";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";

registerLocale("ro", ro);

const EditSSM = ({ isOpen, onClose, recordData, employerData, onSuccess }) => {
  // state pentru stări generale
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [ssmTypesOptions, setSsmTypesOptions] = useState([]);
  const [providersOptions, setProvidersOptions] = useState([]);

  // state pentru formular
  const [formData, setFormData] = useState({
    angajat: "",
    ssm_type: "",
    execution_date: "",
    valid_date: "",
    expire_date: "",
    provider: "",
    alert: 5,
  });

  // referințe către DatePicker
  const executionRef = useRef(null);
  const validRef = useRef(null);
  const expireRef = useRef(null);

  // Stiluri custom pentru react-select - actualizat ca AddSSM
  const getCustomSelectStyles = (fieldName) => ({
    control: (base, state) => ({
      ...base,
      border: fieldErrors[fieldName] ? '1px solid #d32f2f' : 
              state.isFocused ? '1px solid #007BFF' : '1px solid #ccc',
      '&:hover': {
        border: fieldErrors[fieldName] ? '1px solid #d32f2f' : 
                state.isFocused ? '1px solid #007BFF' : '1px solid #888'
      },
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      minHeight: '38px',
      backgroundColor: state.isFocused ? '#fff' : '#fff',
      boxShadow: 'none',
      borderRadius: '4px',
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0 8px',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      textAlign: 'left'
    }),
    input: (base) => ({
      ...base,
      color: '#1a1a1a',
      margin: '0',
      padding: '0',
      '& input': {
        boxShadow: 'none !important',
        border: 'none !important',
        outline: 'none !important',
        padding: '0',
        margin: '0',
        textAlign: 'left'
      }
    }),
    placeholder: (base) => ({
      ...base,
      color: '#999',
      fontSize: '14px',
      textAlign: 'left',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start'
    }),
    singleValue: (base) => ({
      ...base,
      color: '#1a1a1a',
      fontSize: '14px',
      textAlign: 'left',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start'
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      fontSize: '14px',
      textAlign: 'left'
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#e6f2ff' : state.isFocused ? '#f0f0f0' : '#fff',
      color: state.isSelected ? '#006ce4' : '#1a1a1a',
      fontSize: '14px',
      textAlign: 'left',
      '&:active': {
        backgroundColor: '#e6f2ff',
      }
    }),
    clearIndicator: (base) => ({
      ...base,
      color: '#666',
      '&:hover': {
        color: '#333',
      },
      padding: '4px 8px',
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: '#666',
      '&:hover': {
        color: '#333',
      },
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: '100%',
      display: 'flex',
      alignItems: 'center',
    }),
    indicatorSeparator: (base) => ({
      ...base,
      marginTop: '8px',
      marginBottom: '8px',
    }),
  });
  
  // Gestionare modificare select cu react-select
  const handleSelectChange = (field) => (selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: "" }));
  };

  useEffect(() => {
    // la deschiderea modalului, încărcăm date
    if (isOpen && recordData) {
      const fetchData = async () => {
        try {
          // obținem tipuri SSM + furnizori
          const [typesRes, provRes] = await Promise.all([
            axiosInstance.get("/type-ssm/"),
            axiosInstance.get("/suppliers/"),
          ]);

          // Transformă pentru react-select
          const ssmOptions = (typesRes.data || []).map((t) => ({
            value: t.id,
            label: t.type,
          }));
          setSsmTypesOptions(ssmOptions);

          const providerOptions = (provRes.data || []).map((p) => ({
            value: p.id,
            label: p.name,
          }));
          setProvidersOptions(providerOptions);

          // setăm datele existente ale înregistrării
          setFormData({
            angajat: recordData.angajat || employerData?.cod || "",
            ssm_type: recordData.ssm_type || recordData.ssm_type_id || "",
            execution_date: recordData.execution_date || "",
            valid_date: recordData.valid_date || "",
            expire_date: recordData.expire_date || "",
            provider: recordData.provider_id || recordData.provider || "",
            alert: recordData.alert || 5,
          });

          setError(null);
          setFieldErrors({});
        } catch (err) {
          console.error("❌ Eroare la preluare:", err);
          setError("Nu s-au putut încărca datele.");
        }
      };
      fetchData();
    }
  }, [isOpen, recordData, employerData]);

  const handleInputChange = (e) => {
    // actualizăm câmpurile text/select
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: "" }));

    // adăugăm clasa vizuală "filled"
    if (value && value.trim() !== "") e.target.classList.add("filled");
    else e.target.classList.remove("filled");
  };

  const handleDateChange = (field, date) => {
    if (!date) {
      setFormData((prev) => ({ ...prev, [field]: "" }));
      return;
    }

    // Formatăm pentru Django → YYYY-MM-DD
    const formatted = date.toLocaleDateString("en-CA");
    setFormData((prev) => ({ ...prev, [field]: formatted }));
    setFieldErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateForm = () => {
    // validare minimă - la fel ca AddSSM
    const errors = {};
    if (!formData.ssm_type) errors.ssm_type = "SSM Type is required";
    if (!formData.execution_date) errors.execution_date = "Execution date is required";
    if (!formData.valid_date) errors.valid_date = "Valid from date is required";
    if (!formData.expire_date) errors.expire_date = "Valid until date is required";
    if (!formData.provider) errors.provider = "Provider is required";
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    // submit formular
    e.preventDefault();
    setLoading(true);
    setError(null);
    setFieldErrors({});

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      // pregătim payload
      const submitData = {
        employee: employerData?.id,
        ssm_type: parseInt(formData.ssm_type),
        execution_date: formData.execution_date,
        valid_date: formData.valid_date,
        expire_date: formData.expire_date,
        provider: parseInt(formData.provider),
        alert: parseInt(formData.alert) || 5,
      };

      // apel PUT pentru update
      await axiosInstance.put(`ssm/${recordData.id}/`, submitData);

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error("❌ Eroare la actualizare:", err);
      setError("Eroare la salvarea datelor.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null; // ascunde modalul dacă nu e deschis

  return (
    <div className="page-editssm">
      <div className="ssm-modal-overlay">
        <div className="ssm-modal">
          <div className="ssm-header">
            <div className="header-left">
              <h2 className="header-title">Edit SSM Record</h2>
            </div>
            <button onClick={onClose} className="close-btn">×</button>
          </div>

          <hr className="header-divider" />

          {error && <div className="error-box">{error}</div>}

          <form onSubmit={handleSubmit}>
            {/* Tip SSM */}
            <div className="form-group">
              <label className="label-left">
                SSM Type <span className="required">*</span>
              </label>

              <Select
                name="ssm_type"
                value={ssmTypesOptions.find(option => option.value === parseInt(formData.ssm_type))}
                onChange={handleSelectChange("ssm_type")}
                options={ssmTypesOptions}
                placeholder="Select SSM Type"
                className={`multiselect-field ${fieldErrors.ssm_type ? 'select-error' : ''}`}
                classNamePrefix="select"
                isSearchable={true}
                isClearable={true}
                styles={getCustomSelectStyles("ssm_type")}
              />

              {fieldErrors.ssm_type && (
                <span className="field-error error-left">{fieldErrors.ssm_type}</span>
              )}
            </div>

            {/* Date: execuție / valabil de la / valabil până */}
            <div className="form-row">
              {[
                { name: "execution_date", label: "Execution Date", ref: executionRef },
                { name: "valid_date", label: "Valid From", ref: validRef },
                { name: "expire_date", label: "Valid Until", ref: expireRef },
              ].map(({ name, label, ref }) => (
                <div key={name} className="form-group">
                  <label className="label-left">
                    {label} <span className="required">*</span>
                  </label>
                  <div className="datepicker-container">
                    <DatePicker
                      ref={ref}
                      selected={formData[name] ? new Date(formData[name]) : null}
                      onChange={(date) => handleDateChange(name, date)}
                      dateFormat="dd.MM.yyyy"
                      locale="ro"
                      className={`datepicker-input-with-icon ${fieldErrors[name] ? "field-error-border" : ""}`}
                      placeholderText="dd.mm.yyyy"
                      popperPlacement="bottom-start"
                    />
                    <span
                      className="calendar-icon-inside"
                      onClick={() => ref.current?.setOpen(true)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    </span>
                  </div>
                  {fieldErrors[name] && (
                    <span className="field-error error-left">{fieldErrors[name]}</span>
                  )}
                </div>
              ))}
            </div>

            {/* Provider */}
            <div className="form-group">
              <label className="label-left">
                Provider <span className="required">*</span>
              </label>

              <Select
                name="provider"
                value={providersOptions.find(option => option.value === parseInt(formData.provider))}
                onChange={handleSelectChange("provider")}
                options={providersOptions}
                placeholder="Select Provider"
                className={`multiselect-field ${fieldErrors.provider ? 'select-error' : ''}`}
                classNamePrefix="select"
                isSearchable={true}
                isClearable={true}
                styles={getCustomSelectStyles("provider")}
              />

              {fieldErrors.provider && (
                <span className="field-error error-left">{fieldErrors.provider}</span>
              )}
            </div>

            {/* Alert days */}
            <div className="form-group">
              <label className="label-left">Alert (days before expiry)</label>
              <input
                type="number"
                name="alert"
                value={formData.alert}
                onChange={handleInputChange}
                min="1"
                max="365"
                className="input-left"
              />
            </div>

            {/* Butoane */}
            <div className="actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button type="submit" className="create-btn" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 size={16} className="spinner" /> Saving...
                  </>
                ) : (
                  "Save"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditSSM;